import { useCallback, useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Modal } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { Checkbox } from '~/components/common/Checkbox'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'

interface SplunkTestConnectionResponse {
  success: boolean
  message: string
  indices?: string[]
}

interface ManageScopeModalProps {
  open: boolean
  onClose: () => void
  workspaceUuid: string
  currentAllowedIndices: string[]
  onSaved: () => void
}

export function ManageScopeModal({
  open,
  onClose,
  workspaceUuid,
  currentAllowedIndices,
  onSaved
}: ManageScopeModalProps) {
  const [availableIndices, setAvailableIndices] = useState<string[]>([])
  const [selectedIndices, setSelectedIndices] = useState<string[]>([])
  const [isLoadingIndices, setIsLoadingIndices] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const loadIndices = useCallback(async () => {
    setIsLoadingIndices(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<SplunkTestConnectionResponse>(
        `/api/workspace/${workspaceUuid}/integration/splunk_credentials/test`,
        {},
        { credentials: 'include' }
      )
      const fetchedIndices = Array.isArray(data.indices) ? data.indices : []
      const uniqueIndices = [...new Set(fetchedIndices)].sort((a, b) => a.localeCompare(b))
      setAvailableIndices(uniqueIndices)
      if (uniqueIndices.length === 0) {
        BetterToast.error('Connected, but no indices were returned for this API key.')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Failed to load available indices.'
      setErrorMessage(errorText)
    } finally {
      setIsLoadingIndices(false)
    }
  }, [workspaceUuid])

  useEffect(() => {
    if (!open) {
      return
    }

    setSelectedIndices([...currentAllowedIndices])
    setErrorMessage('')
    void loadIndices()
  }, [open, currentAllowedIndices, loadIndices])

  const handleToggleIndex = (indexName: string) => {
    setSelectedIndices(prev =>
      prev.includes(indexName)
        ? prev.filter(existing => existing !== indexName)
        : [...prev, indexName]
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const data = await fetchWrapper.put<{ success: boolean; message: string }>(
        `/api/workspace/${workspaceUuid}/integration/splunk_credentials`,
        { allowed_indices: selectedIndices },
        { credentials: 'include' }
      )
      if (data.success) {
        BetterToast.success('Monitored scope updated successfully!')
        onSaved()
        onClose()
      } else {
        BetterToast.error(data.message || 'Failed to update monitored scope')
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Please try again.'
      BetterToast.error(`Failed to update monitored scope: ${errorText}`)
    } finally {
      setIsSaving(false)
    }
  }

  const sortedIndices = [...availableIndices].sort((a, b) => {
    const aSelected = selectedIndices.includes(a)
    const bSelected = selectedIndices.includes(b)
    if (aSelected !== bSelected) return aSelected ? -1 : 1
    return a.localeCompare(b)
  })

  return (
    <Modal
      className='w-full max-w-[640px] max-h-[calc(100vh-48px)] rounded-[20px] sm:rounded-[28px]'
      isOpen={open}
      onClose={onClose}
      hideCloseButton={false}
    >
      <div className='rounded-t-[20px] bg-white px-4 pt-4 pb-3 sm:rounded-t-[28px] sm:px-6 sm:pt-6 sm:pb-5'>
        <Typography
          fontFamily='geist'
          fontSize='titleSm'
          fontWeight='bold'
          className='text-[rgba(0,0,0,0.88)]'
        >
          Manage Monitored Scope
        </Typography>
      </div>

      <div className='bg-white px-4 pb-4 sm:px-6 sm:pb-6'>
        <div className='flex items-center justify-between pb-3 sm:pb-4'>
          <Typography fontFamily='geist' fontSize='bodySm' className='text-[rgba(0,0,0,0.45)]'>
            {selectedIndices.length} of {availableIndices.length} indices selected
          </Typography>
          <button
            type='button'
            disabled={isLoadingIndices || isSaving}
            onClick={() => void loadIndices()}
            className='text-[#5b5bd5] disabled:opacity-50'
          >
            <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
              {isLoadingIndices ? 'Refreshing...' : 'Refresh'}
            </Typography>
          </button>
        </div>

        {errorMessage ? (
          <div className='mb-3 rounded-xl border border-[#EA463D] bg-[rgba(234,70,45,0.03)] px-4 py-3 sm:mb-4'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-[#D94B36]'
              fontWeight='medium'
            >
              {errorMessage}
            </Typography>
          </div>
        ) : null}

        {sortedIndices.length > 0 ? (
          <div className='max-h-[50vh] overflow-y-auto rounded-xl border border-[#eaeaea] sm:max-h-[400px]'>
            {sortedIndices.map((indexName, i) => {
              const isSelected = selectedIndices.includes(indexName)
              return (
                <div
                  key={indexName}
                  onClick={() => handleToggleIndex(indexName)}
                  className={[
                    'flex h-10 cursor-pointer items-center pl-1 pr-3 sm:h-12',
                    isSelected ? 'bg-[rgba(91,91,213,0.04)]' : 'bg-white',
                    i < sortedIndices.length - 1 ? 'border-b border-[#f0f0f0]' : ''
                  ].join(' ')}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleIndex(indexName)}
                  />
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-[rgba(0,0,0,0.88)]'
                  >
                    {indexName}
                  </Typography>
                </div>
              )
            })}
          </div>
        ) : (
          <div className='flex h-24 items-center justify-center rounded-xl border border-[#eaeaea] sm:h-[120px]'>
            <Typography fontFamily='geist' fontSize='bodySm' className='text-[rgba(0,0,0,0.45)]'>
              {isLoadingIndices
                ? 'Loading available indices...'
                : 'No indices available. Click Refresh to fetch indices.'}
            </Typography>
          </div>
        )}
      </div>

      <div className='flex items-center justify-end gap-3 rounded-b-[20px] bg-white px-4 py-3 border-t border-[#eaeaea] sm:rounded-b-[28px] sm:px-6 sm:py-4'>
        <Button variant='outlined' onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant='primary'
          isDisabled={isSaving || isLoadingIndices}
          onClick={() => void handleSave()}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  )
}
